import { ReactNode } from 'react';
import { WidgetEmpty } from '../WidgetStates';
import { BarChart } from '../../../widgets/shared/chart/BarChart';
import { ProgressChart } from '../../../widgets/shared/chart/ProgressChart';

export type ChartType = 'line' | 'bar' | 'area' | 'pie' | 'donut' | 'progress';

export interface ChartDataSeries {
  name: string;
  data: number[];
}

export interface ChartRendererProps {
  type: ChartType;
  categories: string[];
  series: ChartDataSeries[];
}

// The ChartRenderer acts as a lightweight orchestrator.
// It does NOT contain chart-specific rendering logic, to keep the component simple.
export function ChartRenderer({ type, categories = [], series = [] }: ChartRendererProps) {
  switch (type) {
    case 'bar':
      return <BarChart categories={categories} series={series} />;
    case 'progress':
      return <ProgressChart categories={categories} series={series} />;
    default:
      return <WidgetEmpty message={`${type.charAt(0).toUpperCase()}${type.slice(1)} chart is not available.`} />;
  }
}

export interface ChartCardProps {
  title?: string;
  type: ChartType;
  categories?: string[];
  series?: ChartDataSeries[];
  emptyMessage?: string;
  children?: ReactNode; // Allow custom chart injection to bypass ChartRenderer if strictly needed
}

export function ChartCard({
  title,
  type,
  categories = [],
  series = [],
  emptyMessage = 'No chart data available.',
  children
}: ChartCardProps) {
  const hasData = series.length > 0 && categories.length > 0;

  return (
    <div className="flex flex-col h-full bg-surface rounded-card border border-outline p-4">
      {title && <h4 className="text-body-md font-semibold text-on-surface mb-4">{title}</h4>}
      
      <div className="flex-1 min-h-[200px]">
        {children ? (
          children
        ) : !hasData ? (
          <WidgetEmpty message={emptyMessage} />
        ) : (
          <ChartRenderer type={type} categories={categories} series={series} />
        )}
      </div>
    </div>
  );
}
